'use client';

import { useEffect, useState } from 'react';
import { scheduleIdleCallback } from '@/utils/schedule-idle-callback';

type UseDeferUntilIdleOptions = {
  timeout?: number;
};

let hasReachedIdle = false;

/**
 * Returns `true` after the page has loaded and the browser is idle (or the
 * timeout elapses). Use to defer non-critical client-only UI/hydration work.
 *
 * Once idle has been reached once in this JS context, later mounts return
 * `true` immediately so SPA navigations do not replay the first-load deferral.
 */
const useDeferUntilIdle = ({ timeout = 2000 }: UseDeferUntilIdleOptions = {}) => {
  const [isReady, setIsReady] = useState(hasReachedIdle);

  useEffect(() => {
    if (hasReachedIdle) {
      setIsReady(true);
      return;
    }

    scheduleIdleCallback(
      () => {
        hasReachedIdle = true;
        setIsReady(true);
      },
      { timeout },
    );
  }, [timeout]);

  return isReady;
};

export default useDeferUntilIdle;
